// dealerGuard.ts — Coverage-Matrix Wave 2 (Squad UI-B).
// Frontend mirror of the LOCKED cross-cutting rule (spec §6):
//   A dealer can be assigned to a (productGroup, territory) ONLY IF an EXEC
//   (Layer-1 owner) already owns that same (productGroup, territory). No rep
//   owner ⇒ no one for the dealer to work under.
//
// The backend enforces this authoritatively (422 `NO_EXEC_OWNER`); this module
// is the client-side mirror so every dealer-assign surface can (a) proactively
// disable the action when we already know there's no exec, and (b) render a
// helpful inline message when the server rejects. Pure — no React, no network.

/** Single source of the user-facing copy — reused on every dealer-assign surface. */
export const DEALER_REQUIRES_EXEC_MESSAGE =
  'Assign an exec to this territory first — a dealer can only work under a rep owner.';

/** The backend error code the guard maps to. */
export const NO_EXEC_OWNER_CODE = 'NO_EXEC_OWNER';

/**
 * Detect the backend 422 `NO_EXEC_OWNER` rejection from a caught error.
 * Tolerant of the several shapes an axios error / plain object can take:
 *   err.response.data.code === 'NO_EXEC_OWNER'   (canonical)
 *   err.response.data.error contains NO_EXEC_OWNER
 *   err.code / err.error === 'NO_EXEC_OWNER'      (already-unwrapped)
 * Requires a 422 status when a status is present; a bare code still matches.
 */
export function isNoExecOwnerError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as Record<string, any>;
  const status: number | undefined = e?.response?.status ?? e?.status;
  if (status !== undefined && status !== 422) return false;

  const data = e?.response?.data ?? e?.data ?? e;
  const code = data?.code ?? e?.code;
  if (typeof code === 'string' && code.toUpperCase() === NO_EXEC_OWNER_CODE) return true;

  const errText = data?.error ?? data?.message ?? e?.error ?? e?.message;
  if (typeof errText === 'string' && errText.toUpperCase().includes(NO_EXEC_OWNER_CODE)) return true;

  return false;
}

/**
 * Predicate mirror of the backend rule. `hasExec` comes from the
 * territory-detail endpoint. `true` ⇒ a dealer may be assigned here.
 */
export function canAssignDealerToTerritory(detail: { hasExec: boolean } | null | undefined): boolean {
  return !!detail && detail.hasExec === true;
}

/**
 * Returns the block reason (the guard message) when a dealer-assign should be
 * prevented, or `null` when it's allowed. Use to disable+explain a control.
 * `detail === undefined` ⇒ not yet loaded ⇒ not blocked here (control stays
 * in a loading state; the server 422 remains the backstop).
 */
export function dealerAssignBlockReason(
  detail: { hasExec: boolean } | null | undefined,
): string | null {
  if (detail == null) return null; // unknown/loading — don't hard-block on stale UI
  return detail.hasExec ? null : DEALER_REQUIRES_EXEC_MESSAGE;
}

/**
 * Map a caught dealer-assign error to a friendly message. Returns the
 * dealer-requires-exec copy for a NO_EXEC_OWNER 422, otherwise a generic
 * fallback derived from the error (so callers get one place to translate).
 */
export function dealerAssignErrorMessage(err: unknown, fallback = 'Could not assign dealer.'): string {
  if (isNoExecOwnerError(err)) return DEALER_REQUIRES_EXEC_MESSAGE;
  const e = err as Record<string, any> | null;
  const msg = e?.response?.data?.error ?? e?.response?.data?.message ?? e?.message;
  return typeof msg === 'string' && msg.trim() ? msg : fallback;
}
